import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiCamera, FiUpload, FiChevronRight, FiCpu, FiAlertTriangle, 
  FiVideo, FiFileText, FiPhoneCall, FiCheck, FiX, FiActivity,
  FiHome, FiArrowLeft
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { issueCategories, simulateAdamAI, supervisors } from '../data/mockData';
import { useAppContext } from '../context/AppContext';

export const Troubleshoot = () => {
  const navigate = useNavigate();
  const { addCase } = useAppContext();
  const fileInputRef = useRef(null);

  // Flow State
  const [step, setStep] = useState(0); // 0: Upload, 1: Category, 2: Questions, 3: Analyzing, 4: Results, 5: ActionView
  
  // Data State
  const [image, setImage] = useState(null);
  const [category, setCategory] = useState('');
  const [answers, setAnswers] = useState({ workType: '', symptom: '', env: '' });
  const [aiResult, setAiResult] = useState(null);
  const [activeAction, setActiveAction] = useState(null); // 'video', 'guide', 'call'

  // --- Handlers ---
  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
        setStep(1);
      };
      reader.readAsDataURL(e.target.files[0]);
    } else {
      setImage('https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=400&auto=format&fit=crop');
      setStep(1);
    }
  };

  const startAnalysis = () => {
    setStep(3);
    setTimeout(() => {
      const result = simulateAdamAI(category, answers);
      setAiResult(result);
      setStep(4);
    }, 2500);
  };

  const resolveCase = (status) => {
    addCase({
      id: `CASE-${Math.floor(Math.random() * 10000)}`,
      date: 'Just now',
      issue: aiResult?.diagnosis || category,
      status: status,
      image: image
    });
    navigate('/history');
  };

  // --- Common Components ---
  const FlowHeader = () => (
    <div className="flex items-center justify-between mb-8">
      <button 
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-bold group"
      >
        <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center group-hover:bg-slate-700 transition-colors">
          <FiHome size={16} />
        </div>
        <span>EXIT TO DASHBOARD</span>
      </button>
      
      <div className="flex items-center gap-2">
        {[0, 1, 2, 4].map((s) => (
          <div 
            key={s} 
            className={`h-1 w-8 rounded-full transition-all duration-500 ${
              step >= s ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : 'bg-slate-800'
            }`}
          />
        ))}
      </div>
    </div>
  );

  // --- Steps Components ---
  const StepUpload = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center max-w-md mx-auto">
      <div className="w-20 h-20 bg-blue-600/20 text-blue-400 rounded-full flex items-center justify-center mb-6 ring-4 ring-blue-600/10">
        <FiCpu className="text-4xl" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">AdamAI Field Assist</h2>
      <p className="text-slate-400 mb-8">Upload a photo of the splice, OTDR trace, or cabinet issue and let AI diagnose the problem.</p>
      
      <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handleImageUpload} />
      
      <div className="w-full space-y-4">
        <button onClick={() => fileInputRef.current.click()} className="btn-primary w-full py-4 text-lg">
          <FiCamera className="text-xl" /> Take Photo / Upload
        </button>
        <button onClick={() => handleImageUpload({target:{files:[]}})} className="btn-outline w-full py-3 text-sm opacity-50 hover:opacity-100">
          Use Demo Image
        </button>
      </div>
    </motion.div>
  );

  const StepCategory = () => (
    <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="max-w-lg mx-auto">
      <div className="mb-6 flex items-center gap-4">
        <img src={image} className="w-16 h-16 rounded-lg object-cover border border-slate-700 shadow-lg" alt="uploaded issue" />
        <div>
          <h3 className="text-lg font-bold text-white">What does this relate to?</h3>
          <p className="text-sm text-slate-400">Help narrow down the context.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {issueCategories.map(cat => (
          <button 
            key={cat} 
            onClick={() => { setCategory(cat); setStep(2); }}
            className="glass-panel p-4 text-left rounded-xl hover:bg-slate-800 hover:border-blue-500/50 transition-all flex justify-between items-center group"
          >
            <span className="text-slate-200 text-sm font-medium">{cat}</span>
            <FiChevronRight className="text-slate-500 group-hover:text-blue-400 transition-colors" />
          </button>
        ))}
      </div>
    </motion.div>
  );

  const StepQuestions = () => (
    <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="max-w-lg mx-auto space-y-6">
      <h3 className="text-lg font-bold text-white mb-2">A few quick questions...</h3>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-slate-400 mb-2 block font-medium">What type of work is this?</label>
          <div className="flex gap-2">
            {['Install', 'Repair', 'Maintenance'].map(opt => (
              <button 
                key={opt} 
                onClick={() => setAnswers({ ...answers, workType: opt })}
                className={`flex-1 py-3 rounded-xl text-sm font-bold border transition-all ${
                  answers.workType === opt 
                  ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/20' 
                  : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm text-slate-400 mb-2 block font-medium">Primary Symptom?</label>
          <div className="flex gap-2">
            {['Hard Down', 'Degraded', 'Intermittent'].map(opt => (
              <button 
                key={opt} 
                onClick={() => setAnswers({ ...answers, symptom: opt })}
                className={`flex-1 py-3 rounded-xl text-sm font-bold border transition-all ${
                  answers.symptom === opt 
                  ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/20' 
                  : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>
      <button 
        onClick={startAnalysis}
        disabled={!answers.workType || !answers.symptom}
        className="btn-primary w-full mt-8 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-blue-600/20"
      >
        Analyze Issue <FiActivity className="ml-2 animate-pulse" />
      </button>
    </motion.div>
  );

  const StepAnalyzing = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center h-full min-h-[50vh] text-center">
      <div className="relative mb-8">
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          className="w-24 h-24 border-4 border-blue-500/10 border-t-blue-500 rounded-full"
        />
        <FiCpu className="text-blue-500 text-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2 tracking-tight uppercase">AdamAI is analyzing...</h3>
      <p className="text-slate-400 max-w-xs mx-auto">Cross-referencing image data with known fault signatures and proprietary diagnostic models.</p>
    </motion.div>
  );

  const StepResults = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-6 pb-20">
      <div className="glass-panel p-6 rounded-2xl border-blue-500/30 bg-blue-900/10 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <FiCpu className="text-8xl" />
        </div>
        <div className="flex items-start justify-between mb-4 relative z-10">
          <div>
            <h4 className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-1">Diagnosis Complete</h4>
            <h2 className="text-2xl font-bold text-white leading-tight">{aiResult.diagnosis}</h2>
          </div>
          <div className="bg-slate-900/80 px-4 py-2 rounded-xl border border-white/5 flex items-center gap-2 shadow-xl">
            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Confidence</span>
            <span className="text-emerald-400 font-black text-lg">{aiResult.confidence}%</span>
          </div>
        </div>
        <p className="text-slate-300 text-sm leading-relaxed relative z-10">{aiResult.summary}</p>
      </div>

      <div>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          Recommended Actions
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <button onClick={() => { setActiveAction('video'); setStep(5); }} className="glass-panel p-5 rounded-2xl flex flex-col items-center text-center hover:bg-slate-800 transition-all hover:border-blue-500/30 group">
            <div className="w-12 h-12 bg-indigo-500/20 text-indigo-400 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <FiVideo className="text-xl" />
            </div>
            <h4 className="font-bold text-white mb-1 text-sm uppercase tracking-tight">Watch Fix</h4>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{aiResult.video.duration} runtime</p>
          </button>
          
          <button onClick={() => { setActiveAction('guide'); setStep(5); }} className="glass-panel p-5 rounded-2xl flex flex-col items-center text-center hover:bg-slate-800 transition-all hover:border-amber-500/30 group">
            <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <FiFileText className="text-xl" />
            </div>
            <h4 className="font-bold text-white mb-1 text-sm uppercase tracking-tight">Step Guide</h4>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Read Procedure</p>
          </button>

          <button onClick={() => { setActiveAction('call'); setStep(5); }} className="glass-panel p-5 rounded-2xl flex flex-col items-center text-center hover:bg-slate-800 transition-all border-rose-500/30 hover:border-rose-500/60 group">
            <div className="w-12 h-12 bg-rose-500/20 text-rose-400 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform animate-pulse">
              <FiPhoneCall className="text-xl" />
            </div>
            <h4 className="font-bold text-white mb-1 text-sm uppercase tracking-tight">Escalate</h4>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Call Supervisor</p>
          </button>
        </div>
      </div>

      <div className="glass-panel p-5 rounded-2xl border-white/5">
        <h4 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
          <FiAlertTriangle className="text-amber-500"/> Critical Secondary Checks
        </h4>
        <ul className="space-y-3">
          {aiResult.nextChecks.map((chk, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
              <div className="w-5 h-5 rounded-md bg-slate-800 flex items-center justify-center shrink-0 text-[10px] text-blue-400 font-bold">{i+1}</div>
              {chk}
            </li>
          ))}
        </ul>
      </div>

      <div className="pt-4 flex flex-col sm:flex-row gap-4">
        <button onClick={() => resolveCase('resolved')} className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-500 py-4 shadow-xl shadow-emerald-900/20">
          <FiCheck /> MARK AS RESOLVED
        </button>
        <button onClick={() => resolveCase('unresolved')} className="btn-secondary flex-1 py-4">
          SAVE TO HISTORY
        </button>
      </div>
    </motion.div>
  );

  const ActionVideo = () => (
    <div className="space-y-4 max-w-2xl mx-auto animate-in fade-in">
      <button onClick={() => setStep(4)} className="text-xs font-bold text-blue-400 mb-2 flex items-center gap-2 hover:text-blue-300 uppercase tracking-widest">
        <FiArrowLeft /> Back to Results
      </button>
      <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden relative group flex items-center justify-center border border-slate-700 shadow-2xl">
        <img src={aiResult.video.thumb} className="absolute inset-0 w-full h-full object-cover opacity-60" alt="thumbnail" />
        <button className="relative z-10 w-20 h-20 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-2xl shadow-blue-900/50 hover:scale-110 transition-transform">
          <FiPlay className="text-3xl ml-1" />
        </button>
      </div>
      <div className="p-2">
        <h2 className="text-xl font-bold text-white mb-2">{aiResult.video.title}</h2>
        <p className="text-slate-400 text-sm leading-relaxed">{aiResult.video.desc}</p>
      </div>
    </div>
  );

  const ActionGuide = () => (
    <div className="space-y-6 max-w-2xl mx-auto animate-in slide-in-from-bottom-4">
      <button onClick={() => setStep(4)} className="text-xs font-bold text-blue-400 mb-2 flex items-center gap-2 hover:text-blue-300 uppercase tracking-widest">
        <FiArrowLeft /> Back to Results
      </button>
      <div className="glass-panel p-8 rounded-[32px] border-white/10 shadow-2xl">
        <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-tight">Repair Procedure</h2>
        <div className="mb-8 bg-rose-500/10 border border-rose-500/20 p-5 rounded-2xl flex items-start gap-4">
          <FiAlertTriangle className="text-rose-400 text-2xl shrink-0" />
          <p className="text-sm font-medium text-rose-200">{aiResult.guide.warning}</p>
        </div>
        
        <div className="mb-8">
          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4">Required Field Tools</h4>
          <div className="flex flex-wrap gap-2">
            {aiResult.guide.tools.map(t => (
              <span key={t} className="px-4 py-2 bg-slate-900/50 border border-white/5 rounded-xl text-xs font-bold text-slate-300">{t}</span>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-6">Execution Steps</h4>
          <div className="space-y-6">
            {aiResult.guide.steps.map((step, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-8 h-8 rounded-xl bg-blue-600/20 text-blue-400 flex items-center justify-center text-sm font-black shrink-0 border border-blue-500/20">{i+1}</div>
                <p className="text-sm text-slate-300 pt-1 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <button onClick={() => resolveCase('resolved')} className="btn-primary w-full bg-emerald-600 hover:bg-emerald-500 py-5 text-lg shadow-xl shadow-emerald-900/20">
        <FiCheck /> MARK AS RESOLVED
      </button>
    </div>
  );

  const ActionCall = () => {
    const [calling, setCalling] = useState(true);
    const sup = supervisors[0];

    useEffect(() => {
      const timer = setTimeout(() => setCalling(false), 3000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <div className="fixed inset-0 bg-slate-950 z-50 flex flex-col animate-in zoom-in-95 duration-300">
        <div className="flex-1 relative bg-slate-900 flex items-center justify-center overflow-hidden">
          {calling ? (
            <div className="text-center relative z-10">
              <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 relative">
                <img src={sup.avatar} className="w-full h-full object-cover" alt="sup" />
                <div className="absolute inset-0 border-4 border-blue-500 rounded-full animate-ping opacity-50"></div>
              </div>
              <h2 className="text-3xl font-black text-white mb-2 uppercase tracking-tighter">Connecting...</h2>
              <p className="text-blue-400 font-bold text-sm tracking-widest">{sup.name.toUpperCase()}</p>
            </div>
          ) : (
            <>
              <img src={sup.avatar} className="absolute inset-0 w-full h-full object-cover opacity-60" alt="remote" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-900/30"></div>
              
              <div className="absolute bottom-32 right-6 w-32 h-48 bg-slate-800 rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=150&auto=format&fit=crop" className="w-full h-full object-cover" alt="local" />
              </div>

              <div className="absolute top-8 left-8 flex items-center gap-4 bg-slate-950/50 backdrop-blur-md px-4 py-2 rounded-full border border-white/10">
                <div className="bg-rose-500 w-2.5 h-2.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(244,63,94,0.5)]"></div>
                <span className="text-white font-black text-sm tracking-widest">00:14</span>
              </div>
            </>
          )}
          {/* Futuristic HUD overlay */}
          <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none opacity-20">
            <div className="w-1 h-32 border-l border-white/50"></div>
            <div className="w-1 h-32 border-r border-white/50"></div>
          </div>
        </div>

        <div className="h-32 bg-slate-900/80 backdrop-blur-xl border-t border-white/5 flex items-center justify-center gap-8 pb-safe">
          <button className="w-14 h-14 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center hover:bg-slate-700 border border-white/5 transition-all">
            <FiVideo className="text-xl" />
          </button>
          <button onClick={() => resolveCase('escalated')} className="w-20 h-20 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-2xl shadow-rose-900/40 hover:bg-rose-500 hover:scale-110 transition-all">
            <FiPhoneCall className="text-3xl rotate-[135deg]" />
          </button>
          <button className="w-14 h-14 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center hover:bg-slate-700 border border-white/5 transition-all">
            <FiCamera className="text-xl" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      {/* Show Header for all steps except the full-screen call */}
      {step < 5 || activeAction !== 'call' ? <FlowHeader /> : null}

      <div className="relative">
        {step === 0 && <StepUpload />}
        {step === 1 && <StepCategory />}
        {step === 2 && <StepQuestions />}
        {step === 3 && <StepAnalyzing />}
        {step === 4 && <StepResults />}
        {step === 5 && activeAction === 'video' && <ActionVideo />}
        {step === 5 && activeAction === 'guide' && <ActionGuide />}
        {step === 5 && activeAction === 'call' && <ActionCall />}
      </div>

      {/* Quick reset for dev/demo */}
      {step > 0 && step < 5 && (
        <div className="fixed top-4 right-4 z-40 md:hidden">
          <button onClick={() => setStep(0)} className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-white border border-white/5">
            <FiX />
          </button>
        </div>
      )}
    </div>
  );
};

// Mock FiPlay component for Video thumbnail
const FiPlay = (props) => (
  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M7 6v12l10-6z"></path>
  </svg>
);